import React, { useEffect, useRef, useState } from 'react';

const About = () => {
    const [isVisible, setIsVisible] = useState(false);
    const sectionRef = useRef(null);

    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setIsVisible(true);
                }
            },
            { threshold: 0.1 }
        );

        if (sectionRef.current) {
            observer.observe(sectionRef.current);
        }

        return () => {
            if (sectionRef.current) {
                observer.unobserve(sectionRef.current);
            }
        };
    }, []);

    const values = [
        {
            icon: (
                <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                </svg>
            ),
            title: 'Innovation First',
            description: 'We push the boundaries of technology to deliver groundbreaking solutions that set new industry standards.'
        },
        {
            icon: (
                <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
            ),
            title: 'Client Partnership',
            description: 'We build lasting relationships through transparency, collaboration, and unwavering commitment to client success.'
        },
        {
            icon: (
                <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
                </svg>
            ),
            title: 'Quality Excellence',
            description: 'We maintain the highest standards in code quality, security, and performance across every project we deliver.'
        },
        {
            icon: (
                <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
            ),
            title: 'Agile Delivery',
            description: 'We embrace agile methodologies to ensure rapid, iterative development that adapts to changing business needs.'
        }
    ];

    const achievements = [
        { metric: '98%', label: 'Client Satisfaction Rate' },
        { metric: '2.5x', label: 'Average ROI Increase' },
        { metric: '99.9%', label: 'System Uptime' },
        { metric: '30%', label: 'Faster Time-to-Market' }
    ];

    return (
        <section id="about" ref={sectionRef} className="section-padding bg-slate-50">
            <div className="container-custom">
                {/* Section Header */}
                <div className={`text-center mb-16 ${isVisible ? 'animate-fadeInUp' : 'opacity-0'}`}>
                    <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold text-slate-900 mb-6">
                        About <span className="gradient-text">Scalizap</span>
                    </h2>
                    <div className="w-24 h-1 bg-gradient-to-r from-blue-500 to-cyan-400 mx-auto mb-8"></div>
                    <p className="text-lg md:text-xl text-slate-600 max-w-3xl mx-auto leading-relaxed">
                        Empowering businesses through innovative technology solutions since 2016
                    </p>
                </div>

                {/* Main Content Grid */}
                <div className="grid lg:grid-cols-2 gap-16 items-center mb-20">
                    {/* Company Overview */}
                    <div className={`space-y-6 ${isVisible ? 'animate-slideInLeft' : 'opacity-0'}`} style={{ animationDelay: '0.2s' }}>
                        <h3 className="font-display text-2xl md:text-3xl font-semibold text-slate-900 mb-4 text-left">
                            Building the Digital Future with Precision and Innovation.
                        </h3>
                        <p className="text-slate-600 text-lg leading-relaxed text-left">
                            Established in 2025, Scalizap is a dynamic software manufacturing and sales powerhouse headquartered in the heart
                            of Dubai, UAE. We specialize in the end-to-end creation of high-performance digital solutions, ranging from robust Cloud ERP systems to bespoke web applications.
                        </p>
                        <p className="text-slate-600 text-lg leading-relaxed text-left">
                            As a forward-thinking technology partner, we bridge the gap between complex engineering and user-centric design. Our expertise lies in manufacturing scalable software that empowers businesses to streamline operations,
                            enhance financial accuracy, and drive growth in an increasingly competitive global market.
                        </p>

                        <p className="text-slate-600 text-lg leading-relaxed text-left">
                            By leveraging our strategic location in Dubai's thriving tech ecosystem, we deliver cutting-edge products and dedicated support to a diverse clients. At Scalizap, we don’t just sell software we manufacture the digital future of your business with precision, security, and innovation.
                        </p>
                        <div className="pt-4">
                            <h4 className="font-semibold text-slate-900 mb-3 text-left">Our Mission</h4>
                            <p className="text-slate-600 leading-relaxed text-left">
                                Empowering global businesses by delivering scalable, secure, and user-centric software through technical excellence.
                            </p>
                        </div>
                        <div className="pt-4">
                            <h4 className="font-semibold text-slate-900 mb-3 text-left">Our Vision</h4>
                            <p className="text-slate-600 leading-relaxed text-left">
                                Manufacturing the digital future through innovative, secure, and scalable high-performance software solutions.
                            </p>
                        </div>
                    </div>

                    {/* Achievement Metrics */}
                    <div className={`${isVisible ? 'animate-slideInRight' : 'opacity-0'}`} style={{ animationDelay: '0.4s' }}>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                            {achievements.map((achievement, index) => (
                                <div key={index} className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 text-center card-hover">
                                    <div className="text-3xl md:text-4xl font-bold gradient-text font-display mb-2">
                                        {achievement.metric}
                                    </div>
                                    <div className="text-slate-600 font-medium">
                                        {achievement.label}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Core Values */}
                <div className={`${isVisible ? 'animate-fadeInUp' : 'opacity-0'}`} style={{ animationDelay: '0.6s' }}>
                    <div className="text-center mb-12">
                        <h3 className="font-display text-3xl md:text-4xl font-semibold text-slate-900 mb-4">
                            Our Core Values
                        </h3>
                        <p className="text-slate-600 text-lg max-w-2xl mx-auto">
                            The principles that guide every decision we make and every solution we create
                        </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
                        {values.map((value, index) => (
                            <div key={index} className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 text-center card-hover group">
                                <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-cyan-400 rounded-xl flex items-center justify-center mx-auto mb-6 text-white group-hover:scale-110 transition-transform duration-300">
                                    {value.icon}
                                </div>
                                <h4 className="font-semibold text-slate-900 text-xl mb-4">
                                    {value.title}
                                </h4>
                                <p className="text-slate-600 leading-relaxed">
                                    {value.description}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Why Choose Us */}
                <div className={`mt-20 text-center ${isVisible ? 'animate-fadeInUp' : 'opacity-0'}`} style={{ animationDelay: '0.8s' }}>
                    <div className="bg-gradient-to-br from-slate-900 to-slate-800 rounded-3xl p-8 md:p-12 text-white">
                        <h3 className="font-display text-3xl md:text-4xl font-semibold mb-6">
                            Why Choose Scalizap?
                        </h3>
                        <div className="grid md:grid-cols-3 gap-8 text-left">
                            <div>
                                <h4 className="font-semibold text-xl mb-3 text-cyan-300">Deep Technical Expertise</h4>
                                <p className="text-gray-300">
                                    8+ years of experience across multiple industries with a proven track record of delivering
                                    complex, scalable solutions that drive business growth.
                                </p>
                            </div>
                            <div>
                                <h4 className="font-semibold text-xl mb-3 text-cyan-300">End-to-End Solutions</h4>
                                <p className="text-gray-300">
                                    From initial concept to ongoing support, we provide comprehensive services that cover
                                    every aspect of your digital transformation journey.
                                </p>
                            </div>
                            <div>
                                <h4 className="font-semibold text-xl mb-3 text-cyan-300">Future-Proof Technology</h4>
                                <p className="text-gray-300">
                                    We leverage cutting-edge technologies and industry best practices to ensure your
                                    solutions remain competitive and scalable for years to come.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default About;
